<template>
  <ElForm ref="reportForm" :model="store.formData" :rules="formRules" class="centered-form" @submit.prevent="submitForm">
    <!-- Step 0: User Information -->
    <div v-if="store.currentStep === 0">
      <h3 class="mb-20px">User Information</h3>

      <UserInformationStep />
    </div>

    <!-- Step 1: Basic project information + Site data -->
    <div v-if="store.currentStep === 1">
      <h3 class="mb-20px">Project information & site data</h3>

      <ProjectBasicInfoStep ref="projectBasicInfo" />
    </div>

    <!-- Step 2: Pre-development -->
    <div v-if="store.currentStep === 2">
      <h3 class="mb-20px">Pre-development</h3>

      <SurfaceCompositionBase
        title="Surface composition before development"
        description=""
        surface-type="pre_development_surfaces"
        :show-stormwater-target="false"
      />
    </div>

    <!-- Step 3: Post-development -->
    <div v-if="store.currentStep === 3">
      <h3 class="mb-20px">Post-development</h3>

      <SurfaceCompositionBase
        title="Surface composition after development"
        surface-type="post_development_surfaces"
        :show-stormwater-target="true"
      />
    </div>

    <!-- Step 4: Design storm frequency -->
    <div v-if="store.currentStep === 4">
      <h3 class="mb-20px">Design storm frequency</h3>

      <StormFrequencyComposition />
    </div>
  </ElForm>
</template>

<script setup>
import { ref, reactive, watch, nextTick } from "vue";
import { ElForm } from "element-plus";
import { showToast } from "@/utilities/toast";
import UserInformationStep from "@/views/NewReportView/components/UserInformationStep.vue";
import ProjectBasicInfoStep from "@/views/NewReportView/components/ProjectBasicInfoStep.vue";
import SurfaceCompositionBase from "@/views/NewReportView/components/SurfaceCompositionBase.vue";
import StormFrequencyComposition from "@/views/NewReportView/components/StormFrequencyComposition.vue";
import { useReportFormStore } from "@/stores/reportFormStore";

// Use the Pinia store
const store = useReportFormStore();

const emit = defineEmits(["submit-form"]);

// Form validation rules
const formRules = reactive({
  user_role: [{ required: true, message: "Please select your role", trigger: "change" }],
  project_type: [{ required: true, message: "Please select a project type", trigger: "change" }],
  location_type: [{ required: true, message: "Please select a location type", trigger: "change" }],
  project_name: [{ required: true, message: "Please enter a project name", trigger: "blur" }],
  project_location: [{ required: true, message: "Please enter a project location", trigger: "blur" }],
  property_boundary: [{ required: true, message: "Please draw the property boundary", trigger: "change" }],
  site_area: [
    { required: true, message: "Please enter the site area", trigger: "blur" },
    {
      validator: (_, value, callback) => {
        if (value && parseFloat(value) <= 0) {
          callback(new Error("Site area must be greater than 0"));
        } else {
          callback();
        }
      },
      trigger: "blur",
    },
  ],
  land_use_type: [{ required: true, message: "Please select a land use type", trigger: "change" }],
  design_storm_frequency: [{ required: true, message: "Please select a design storm frequency", trigger: "change" }],
  pre_development_surfaces: [
    {
      validator: (_, value, callback) => {
        if (value.some((surface) => !surface.type)) {
          callback(new Error("All surface types must be selected"));
        } else {
          // Get total percentage from store computed
          const totalPercentage = store.totalSurfacePercentage;
          if (totalPercentage !== 100) {
            callback(new Error("Surface percentages must equal 100%"));
          } else {
            callback();
          }
        }
      },
      trigger: "change",
    },
  ],
  post_development_surfaces: [
    {
      validator: (_, value, callback) => {
        if (value.some((surface) => !surface.type)) {
          callback(new Error("All surface types must be selected"));
        } else {
          // Get total percentage from store computed
          const totalPercentage = store.totalPostDevelopmentSurfacePercentage;
          if (totalPercentage !== 100) {
            callback(new Error("Post-development surface percentages must equal 100%"));
          } else {
            callback();
          }
        }
      },
      trigger: "change",
    },
  ],
  rainfall_intensity: [
    { required: true, message: "Please enter the rainfall intensity", trigger: "blur" },
    {
      validator: (_, value, callback) => {
        if (value && parseFloat(value) <= 0) {
          callback(new Error("Rainfall intensity must be greater than 0"));
        } else {
          callback();
        }
      },
      trigger: "blur",
    },
  ],
  storm_frequencies: [
    {
      validator: (_, value, callback) => {
        if (!value || value.length === 0) {
          callback(new Error("At least one storm frequency is required"));
          return;
        }

        // Check if all storms have required fields
        const hasInvalidStorms = value.some(
          (storm) => !storm.frequency || !storm.duration || !storm.rainfall_intensity
        );

        if (hasInvalidStorms) {
          callback(new Error("All storm frequencies must have rainfall intensity data"));
          return;
        }

        callback();
      },
      trigger: "change",
    },
  ],
});

// Define the form reference
const reportForm = ref(null);
// Reference to the ProjectBasicInfo component
const projectBasicInfo = ref(null);

const validateCurrentStep = () => {
  if (!reportForm.value) return false;

  return new Promise((resolve) => {
    // Step 0: User Information validation
    if (store.currentStep === 0) {
      reportForm.value.validateField(["user_role", "project_type", "location_type"], (valid) => {
        if (!valid) {
          showToast("Please fill in all required fields correctly", { type: "error" });
        }
        resolve(valid);
      });
    }
    // Special handling for step 1 - need to validate property boundary drawing + site data
    else if (store.currentStep === 1) {
      // First validate form fields including site data
      reportForm.value.validateField(["project_name", "project_location", "site_area", "land_use_type"], async (valid) => {
        if (!valid) {
          showToast("Please fill in all required fields correctly", { type: "error" });
          resolve(false);
          return;
        }

        // Then validate the property boundary
        if (store.formData.latitude && store.formData.longitude) {
          // Only check boundary if we have coordinates (map is showing)
          const boundaryValid = projectBasicInfo.value.mapboxInput.validateBoundary();
          if (!boundaryValid) {
            showToast("Please draw the property boundary", { type: "error" });
            resolve(false);
            return;
          }
        } else {
          showToast("Please select a location first", { type: "error" });
          resolve(false);
          return;
        }

        resolve(true);
      });
    } else {
      // For other steps, use the normal validation
      reportForm.value.validateField(
        [
          ...(store.currentStep === 2 ? ["pre_development_surfaces"] : []),
          ...(store.currentStep === 3 ? ["post_development_surfaces"] : []),
          ...(store.currentStep === 4 ? ["design_storm_frequency"] : []),
        ],
        (valid) => {
          if (!valid) {
            showToast("Please fill in all required fields correctly", { type: "error" });
          }
          resolve(valid);
        }
      );
    }
  });
};

// Watch for form validity
watch(
  () => ({
    userRole: store.formData.user_role,
    projectType: store.formData.project_type,
    locationType: store.formData.location_type,
    projectName: store.formData.project_name,
    projectLocation: store.formData.project_location,
    propertyBoundary: store.formData.property_boundary,
    siteArea: store.formData.site_area,
    landUseType: store.formData.land_use_type,
    stormFrequencies: store.formData.storm_frequencies,
    preDevelopmentSurfaces: store.formData.pre_development_surfaces,
    totalSurfacePercentage: store.totalSurfacePercentage,
    postDevelopmentSurfaces: store.formData.post_development_surfaces,
    totalPostDevelopmentSurfacePercentage: store.totalPostDevelopmentSurfacePercentage,
    stormwaterTarget: store.formData.stormwater_target_m3,
  }),
  (newValues) => {
    // Check if all storm frequencies are valid
    const areStormFrequenciesValid = newValues.stormFrequencies.every(
      (storm) => storm.frequency && storm.duration && storm.rainfall_intensity
    );

    // Calculate if form is valid
    const isValid =
      newValues.userRole &&
      newValues.projectType &&
      newValues.locationType &&
      newValues.projectName &&
      newValues.projectLocation &&
      newValues.propertyBoundary &&
      newValues.siteArea &&
      newValues.landUseType &&
      areStormFrequenciesValid &&
      newValues.totalSurfacePercentage === 100 &&
      !newValues.preDevelopmentSurfaces.some((surface) => !surface.type) &&
      newValues.totalPostDevelopmentSurfacePercentage === 100 &&
      !newValues.postDevelopmentSurfaces.some((surface) => !surface.type);

    // Update the store validity
    store.updateFormValidity(isValid);
  },
  { deep: true }
);

// Submit form
const submitForm = () => {
  emit("submit-form", store.formData);
};

// Modify the autofill method
const autofill = (data) => {
  // Use the store's autofill method
  store.autofillForm(data);

  // Wait for Vue to update the data first
  nextTick().then(() => {
    // Make sure the ref is available before using it
    if (projectBasicInfo.value?.mapboxInput) {
      // Directly update the map component
      projectBasicInfo.value.mapboxInput.handleMapRefresh();
    }
  });
};

// Expose methods to parent component
defineExpose({
  validateCurrentStep,
  autofill,
});
</script>

<style lang="scss" scoped>
.centered-form {
  max-width: 740px !important;
}

:deep(.el-form) {
  margin: 0 auto !important;
  padding: 0 !important;
  width: 100% !important;
  display: block !important;
}
</style>
